import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FORM_ENDPOINT, isFormConfigured } from "@/config";
import {
  INQUIRY_CATEGORIES,
  PREFERRED_CONTACT_METHODS,
  ATM_PLACEMENT_TYPES,
  NOVA_SERVICE_TYPES,
  MOVING_PROPERTY_SIZES,
} from "@/data/intake";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  category: "",
  message: "",
  preferredContact: "",
  consent: false,
  // ATM
  atmVenue: "",
  atmLocation: "",
  atmPlacement: "",
  atmAttendance: "",
  atmEventDate: "",
  // Moving
  movingDate: "",
  movingPickup: "",
  movingDestination: "",
  movingPropertySize: "",
  movingMovers: "",
  // Nova
  novaServiceType: "",
};

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldCls =
  "h-11 rounded-lg border-slate-200 focus-visible:ring-blue-600";
const selectCls =
  "flex h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600";

export const ContactForm = ({ presetCategory = "", presetNonce = 0 }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");
  const categoryRef = useRef(null);

  const update = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  // Apply category preset coming from the service directory cards or a
  // ?category= link. Re-applies whenever the preset value OR nonce changes.
  useEffect(() => {
    if (presetCategory) {
      setForm((f) => ({ ...f, category: presetCategory }));
      setErrors((prev) => ({ ...prev, category: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetNonce, presetCategory]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your full name.";
    if (!form.email.trim()) e.email = "Please enter your email address.";
    else if (!emailRe.test(form.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!form.phone.trim()) e.phone = "Please enter a phone number.";
    if (!form.category) e.category = "Please choose an inquiry category.";
    if (!form.message.trim()) e.message = "Please tell us how we can help.";
    if (!form.preferredContact)
      e.preferredContact = "Please select a preferred contact method.";
    if (!form.consent)
      e.consent = "Please confirm that AMS may respond to your inquiry.";
    return e;
  };

  // Build a Formspree-friendly JSON payload with human-readable field
  // names. Keys become the labels shown in the Formspree submission/email.
  const buildPayload = () => {
    const p = {
      // Inquiry category kept prominent (first) and in the subject line.
      "Inquiry Category": form.category,
      "Full Name": form.name.trim(),
      "Email Address": form.email.trim(),
      "Phone Number": form.phone.trim(),
      "Company / Organization": form.company.trim() || "—",
      "Preferred Contact Method": form.preferredContact,
      Message: form.message.trim(),
      "Consent to be contacted": form.consent ? "Yes" : "No",
      // Hidden source identifier.
      Source: "AMS Venture Holdings Website",
      // Formspree special fields.
      _subject: `New inquiry (${form.category}) — AMS Venture Holdings Website`,
      _replyto: form.email.trim(),
    };

    if (form.category === "AMS ATM Services") {
      Object.assign(p, {
        "ATM — Venue or Business Type": form.atmVenue,
        "ATM — Location": form.atmLocation,
        "ATM — Placement Type": form.atmPlacement,
        "ATM — Estimated Attendance / Traffic": form.atmAttendance,
        "ATM — Event Date": form.atmEventDate,
      });
    } else if (form.category === "Professional Moving Services") {
      Object.assign(p, {
        "Moving — Date": form.movingDate,
        "Moving — Pickup Area": form.movingPickup,
        "Moving — Destination Area": form.movingDestination,
        "Moving — Property Size": form.movingPropertySize,
        "Moving — Movers Requested": form.movingMovers,
      });
    } else if (
      form.category === "Nova SecureNet Website and Digital Security Services"
    ) {
      p["Nova — Service Needed"] = form.novaServiceType;
    }
    return p;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return; // guard against duplicates

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      toast.error("Please fix the highlighted fields.");
      const firstKey = Object.keys(found)[0];
      const el = document.getElementById(firstKey);
      if (el && el.focus) el.focus();
      return;
    }

    if (!isFormConfigured()) {
      setStatus("error");
      setServerError("");
      toast.error("The contact form is not configured yet.");
      return;
    }

    setStatus("submitting");
    setServerError("");
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.ok === false) {
        // Surface Formspree validation errors through the error state.
        const msg = Array.isArray(data.errors)
          ? data.errors
              .map((e) => e.message || `${e.field || "Field"} is invalid`)
              .join(" ")
          : data.error || `Request failed with status ${res.status}`;
        setServerError(msg);
        setStatus("error");
        toast.error("We couldn't send your message.");
        return;
      }

      setStatus("success");
      setForm(initial);
      toast.success("Your inquiry has been sent.");
    } catch (err) {
      setServerError("");
      setStatus("error");
      toast.error("We couldn't send your message. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success"
        role="status"
        className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
          <CheckCircle2 className="h-7 w-7 text-blue-600" />
        </div>
        <h3 className="mt-6 font-heading text-2xl font-semibold tracking-tight text-slate-900">
          Thank you for reaching out
        </h3>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Your inquiry has been received. A member of the AMS team will follow
          up using your preferred contact method.
        </p>
        <Button
          data-testid="send-another-btn"
          variant="outline"
          className="mt-7 rounded-full"
          onClick={() => setStatus("idle")}
        >
          Send another inquiry
        </Button>
      </div>
    );
  }

  const showAtm = form.category === "AMS ATM Services";
  const showMoving = form.category === "Professional Moving Services";
  const showNova =
    form.category === "Nova SecureNet Website and Digital Security Services";

  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <p
        role="alert"
        data-testid={`error-${name}`}
        className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600"
      >
        <AlertCircle className="h-3.5 w-3.5" /> {errors[name]}
      </p>
    ) : null;

  return (
    <form
      data-testid="contact-form"
      onSubmit={onSubmit}
      noValidate
      className="rounded-2xl border border-slate-200 bg-white p-6 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
    >
      {status === "error" && (
        <div
          role="alert"
          data-testid="contact-error"
          className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {!isFormConfigured()
              ? "This form isn't connected to a form service yet. Set REACT_APP_FORM_ENDPOINT to enable submissions."
              : serverError ||
                "Something went wrong sending your inquiry. Please try again in a moment."}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-slate-700">Full name *</Label>
          <Input
            id="name"
            name="name"
            data-testid="contact-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Your full name"
            aria-required="true"
            aria-invalid={!!errors.name}
            className={`mt-2 ${fieldCls}`}
          />
          <ErrorMsg name="name" />
        </div>
        <div>
          <Label htmlFor="email" className="text-slate-700">Email address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            data-testid="contact-email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            aria-required="true"
            aria-invalid={!!errors.email}
            className={`mt-2 ${fieldCls}`}
          />
          <ErrorMsg name="email" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-slate-700">Phone number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            data-testid="contact-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(780) 000-0000"
            aria-required="true"
            aria-invalid={!!errors.phone}
            className={`mt-2 ${fieldCls}`}
          />
          <ErrorMsg name="phone" />
        </div>
        <div>
          <Label htmlFor="company" className="text-slate-700">
            Company or organization
          </Label>
          <Input
            id="company"
            name="company"
            data-testid="contact-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Optional"
            className={`mt-2 ${fieldCls}`}
          />
        </div>
      </div>

      {/* Inquiry category */}
      <div className="mt-5">
        <Label htmlFor="category" className="text-slate-700">
          Inquiry category *
        </Label>
        <select
          id="category"
          name="category"
          ref={categoryRef}
          data-testid="contact-category"
          value={form.category}
          onChange={update("category")}
          aria-required="true"
          aria-invalid={!!errors.category}
          className={`mt-2 ${selectCls}`}
        >
          <option value="">Select a category</option>
          {INQUIRY_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <ErrorMsg name="category" />
      </div>

      {/* Conditional: ATM */}
      {showAtm && (
        <fieldset
          data-testid="conditional-atm"
          className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-5"
        >
          <legend className="px-2 text-sm font-semibold text-slate-700">
            ATM details (optional)
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-2">
            <div>
              <Label htmlFor="atmVenue" className="text-slate-700">Venue or business type</Label>
              <Input id="atmVenue" name="atmVenue" data-testid="atm-venue" value={form.atmVenue} onChange={update("atmVenue")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div>
              <Label htmlFor="atmLocation" className="text-slate-700">Location</Label>
              <Input id="atmLocation" name="atmLocation" data-testid="atm-location" value={form.atmLocation} onChange={update("atmLocation")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div>
              <Label htmlFor="atmPlacement" className="text-slate-700">Placement type</Label>
              <select id="atmPlacement" name="atmPlacement" data-testid="atm-placement" value={form.atmPlacement} onChange={update("atmPlacement")} className={`mt-2 ${selectCls}`}>
                <option value="">Select</option>
                {ATM_PLACEMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="atmAttendance" className="text-slate-700">Estimated attendance / traffic</Label>
              <Input id="atmAttendance" name="atmAttendance" data-testid="atm-attendance" value={form.atmAttendance} onChange={update("atmAttendance")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="atmEventDate" className="text-slate-700">Event date (if applicable)</Label>
              <Input id="atmEventDate" name="atmEventDate" type="date" data-testid="atm-event-date" value={form.atmEventDate} onChange={update("atmEventDate")} className={`mt-2 ${fieldCls}`} />
            </div>
          </div>
        </fieldset>
      )}

      {/* Conditional: Moving */}
      {showMoving && (
        <fieldset
          data-testid="conditional-moving"
          className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-5"
        >
          <legend className="px-2 text-sm font-semibold text-slate-700">
            Moving details (optional)
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-2">
            <div>
              <Label htmlFor="movingDate" className="text-slate-700">Moving date</Label>
              <Input id="movingDate" name="movingDate" type="date" data-testid="moving-date" value={form.movingDate} onChange={update("movingDate")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div>
              <Label htmlFor="movingMovers" className="text-slate-700">Number of movers requested</Label>
              <Input id="movingMovers" name="movingMovers" type="number" min="0" data-testid="moving-movers" value={form.movingMovers} onChange={update("movingMovers")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div>
              <Label htmlFor="movingPickup" className="text-slate-700">Pickup area</Label>
              <Input id="movingPickup" name="movingPickup" data-testid="moving-pickup" value={form.movingPickup} onChange={update("movingPickup")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div>
              <Label htmlFor="movingDestination" className="text-slate-700">Destination area</Label>
              <Input id="movingDestination" name="movingDestination" data-testid="moving-destination" value={form.movingDestination} onChange={update("movingDestination")} className={`mt-2 ${fieldCls}`} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="movingPropertySize" className="text-slate-700">Property size</Label>
              <select id="movingPropertySize" name="movingPropertySize" data-testid="moving-property-size" value={form.movingPropertySize} onChange={update("movingPropertySize")} className={`mt-2 ${selectCls}`}>
                <option value="">Select</option>
                {MOVING_PROPERTY_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>
      )}

      {/* Conditional: Nova */}
      {showNova && (
        <fieldset
          data-testid="conditional-nova"
          className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-5"
        >
          <legend className="px-2 text-sm font-semibold text-slate-700">
            Nova SecureNet service (optional)
          </legend>
          <div className="mt-2">
            <Label htmlFor="novaServiceType" className="text-slate-700">Service needed</Label>
            <select id="novaServiceType" name="novaServiceType" data-testid="nova-service-type" value={form.novaServiceType} onChange={update("novaServiceType")} className={`mt-2 ${selectCls}`}>
              <option value="">Select a service</option>
              {NOVA_SERVICE_TYPES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </fieldset>
      )}

      {/* Message */}
      <div className="mt-5">
        <Label htmlFor="message" className="text-slate-700">Message *</Label>
        <Textarea
          id="message"
          name="message"
          data-testid="contact-message"
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us how we can help…"
          rows={5}
          aria-required="true"
          aria-invalid={!!errors.message}
          className="mt-2 rounded-lg border-slate-200 focus-visible:ring-blue-600 resize-none"
        />
        <ErrorMsg name="message" />
      </div>

      {/* Preferred contact method */}
      <fieldset className="mt-6">
        <legend className="text-sm font-medium text-slate-700">
          Preferred contact method *
        </legend>
        <div
          className="mt-3 flex flex-wrap gap-3"
          role="radiogroup"
          aria-label="Preferred contact method"
        >
          {PREFERRED_CONTACT_METHODS.map((m) => (
            <label
              key={m.value}
              className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${
                form.preferredContact === m.value
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="preferredContact"
                value={m.value}
                data-testid={`preferred-${m.value.toLowerCase()}`}
                checked={form.preferredContact === m.value}
                onChange={update("preferredContact")}
                className="h-4 w-4 accent-blue-600"
              />
              {m.label}
            </label>
          ))}
        </div>
        <ErrorMsg name="preferredContact" />
      </fieldset>

      {/* Consent */}
      <div className="mt-6">
        <label
          htmlFor="consent"
          className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer"
        >
          <input
            id="consent"
            name="consent"
            type="checkbox"
            data-testid="contact-consent"
            checked={form.consent}
            onChange={update("consent")}
            aria-required="true"
            aria-invalid={!!errors.consent}
            className="mt-0.5 h-4 w-4 accent-blue-600"
          />
          <span>
            I consent to AMS Venture Holdings Inc. contacting me in response to
            this inquiry. *
          </span>
        </label>
        <ErrorMsg name="consent" />
      </div>

      <Button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "submitting"}
        className="mt-7 h-12 w-full rounded-full bg-slate-900 hover:bg-blue-600 text-white text-base transition-colors duration-300 disabled:opacity-70"
      >
        {status === "submitting" ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </span>
        ) : (
          "Send Inquiry"
        )}
      </Button>
      <p className="mt-4 text-xs text-slate-400 text-center">
        We typically respond within one business day.
      </p>
    </form>
  );
};
